<template>
    <div class="profile-tabs-wrapper" ref="profileTabsWrapperRef" :class="{ 'is-measured': measured }">
        <div class="profile-tabs">
            <div
                    v-for="tab in visibleTabs"
                    :key="tab.id"
                    class="profile-tab"
                    :class="{ active: activeTab === tab.id, 'no-icon': !tab.icon }"
                    @click="selectTab(tab.id)"
                    :title="tab.id"
            >
                <Icon v-if="tab.icon" :class="{ 'tab-icon': tab.icon }" :name="tab.icon" size="md" />
                <span>{{ tab.label }}</span>
            </div>
            <EjsDropdownbutton
                    v-if="hasOverflow"
                    ref="moreButtonRef"
                    :items="overflowTabs"
                    cssClass="profile-tabs-more-btn e-caret-hide"
                    @select="handleOverflowSelect"
                    @open="onDropdownOpen"
            >
                <Icon name="chevrons-right"
                       color="var(--grayscale-700)"
                       size="xl"
                       title="More">
                </Icon>
            </EjsDropdownbutton>
        </div>
    </div>
</template>

<script setup>
import {
    ref, computed, watch, onMounted, onUnmounted, nextTick
} from 'vue';
import Icon from '@common/icon/Icon.vue';
import { iconRegistry } from '@common/icon/iconRegistry';
import EjsDropdownbutton from '@common/syncfusion/dropdownButton';
import mockConfig from '../mockConfig';

const props = defineProps({
    tabs: {
        type: Array,
        default: () => mockConfig.tabs
    },
    activeTab: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['change-tab']);

const internalActiveTab = ref(props.activeTab || (props.tabs.length ? props.tabs[0].id : null));
const measured = ref(false);
const visibleCount = ref(Infinity);
const tabWidths = [];
const profileTabsWrapperRef = ref(null);
const moreButtonRef = ref(null);
let resizeObserver = null;

const activeTab = computed(() => props.activeTab || internalActiveTab.value);

const visibleTabs = computed(() => props.tabs.slice(0, visibleCount.value));
const hasOverflow = computed(() => props.tabs.length > visibleCount.value);
const overflowTabs = computed(() => props.tabs.slice(visibleCount.value).map((tab) => {
    let css = '';
    if (tab.icon) {
        if (tab.icon.startsWith('fa-')) {
            css = tab.icon;
        } else if (iconRegistry[tab.icon]) {
            css = iconRegistry[tab.icon];
        } else {
            css = `e-icons e-${tab.icon}`;
        }
    }
    return {
        text: tab.label,
        additionalData: { id: tab.id },
        iconCss: css
    };
}));

function selectTab(selectedTabId) {
    const tab = props.tabs.find((t) => t.id === selectedTabId);
    if (tab) {
        internalActiveTab.value = tab.id;
        emit('change-tab', tab.id);
    }
}

function handleOverflowSelect(args) {
    const id = args.item?.additionalData?.id;
    if (id) {
        selectTab(id);
    }
}

function onDropdownOpen(args) {
    if (args.element && moreButtonRef.value) {
        // args.element is the <ul>, so we need to target the parent wrapper div
        const popupEl = args.element.closest('.e-dropdown-popup') || args.element.parentElement;
        if (popupEl) {
            popupEl.classList.add('profile-tabs-dropdown-popup');
        }
    }
}

function cacheWidths() {
    if (!profileTabsWrapperRef.value) return;
    const tabElements = profileTabsWrapperRef.value.querySelectorAll('.profile-tab');
    tabWidths.length = 0;
    for (const btn of tabElements) {
        tabWidths.push(btn.offsetWidth);
    }
}

function measure() {
    if (!profileTabsWrapperRef.value || !tabWidths.length) return;

    const available = profileTabsWrapperRef.value.clientWidth;
    const gap = 16;

    const totalWidth = tabWidths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0);
    if (totalWidth <= available) {
        visibleCount.value = tabWidths.length;
        return;
    }

    const moreBtnWidth = moreButtonRef.value?.$el?.offsetWidth ?? 80;
    let accumulated = moreBtnWidth;
    let count = 0;

    for (const width of tabWidths) {
        accumulated += gap + width;
        if (accumulated > available) break;
        count++;
    }
    visibleCount.value = count;
}

onMounted(() => {
    resizeObserver = new ResizeObserver(() => requestAnimationFrame(measure));
    requestAnimationFrame(() => {
        if (!profileTabsWrapperRef.value) return;
        cacheWidths();
        measure();
        resizeObserver.observe(profileTabsWrapperRef.value);
        nextTick(() => {
            requestAnimationFrame(() => {
                measure();
                measured.value = true;
            });
        });
    });
});

onUnmounted(() => {
    resizeObserver?.disconnect();
});

watch(() => props.tabs, (newTabs) => {
    if (newTabs && newTabs.length > 0) {
        const exists = newTabs.find((t) => t.id === activeTab.value);
        if (!exists) {
            selectTab(newTabs[0].id);
        }
    }

    if (!profileTabsWrapperRef.value) return;
    measured.value = false;
    visibleCount.value = Infinity;
    resizeObserver?.disconnect();
    nextTick(() => {
        requestAnimationFrame(() => {
            cacheWidths();
            measure();
            nextTick(() => {
                requestAnimationFrame(() => {
                    measure();
                    measured.value = true;
                    if (profileTabsWrapperRef.value) resizeObserver?.observe(profileTabsWrapperRef.value);
                });
            });
        });
    });
}, { deep: true });

const initialTabExists = props.tabs.find((t) => t.id === activeTab.value);
if (!initialTabExists && props.tabs.length > 0) {
    selectTab(props.tabs[0].id);
} else if (activeTab.value) {
    selectTab(activeTab.value);
}
</script>

<style scoped>
.profile-tabs-wrapper {
    border-bottom: 1px solid var(--border-color-default);
    border-top: 1px solid var(--border-color-default);
    padding-left: var(--space-4);
    opacity: 0;
    transition: opacity 0.15s ease;
    overflow: hidden;
}
.profile-tabs-wrapper.is-measured {
    opacity: 1;
}
.profile-tabs {
    display: flex;
    align-items: center;
    white-space: nowrap;
}
.profile-tab {
    color: var(--text-color-body);
    cursor: pointer;
    font-size: var(--text-size-headline);
    font-weight: var(--text-weight-medium);
    padding: var(--space-5);
    margin: var(--space-2) var(--space-4) var(--space-4);
    letter-spacing: 0.2px;
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: var(--space-4);
    transition: color 180ms ease;
}
.profile-tab::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -1px;
    width: 100%;
    height: 3px;
    background-color: var(--border-color-default-primary);
    border-radius: var(--radius-3xs);
    transform: scaleX(0);
    transform-origin: left;
    opacity: 0;
    transition:
            transform 240ms cubic-bezier(.2,.8,.2,1),
            opacity 180ms ease;
}
.profile-tab.no-icon {
    padding-left: var(--space-3);
    padding-right: var(--space-3);
}
.profile-tab.active {
    color: var(--primary-700);
}
.profile-tab.active .tab-icon {
    color: var(--primary-700) !important;
}
.profile-tab.active::after {
    transform: scaleX(1);
    opacity: 1;
}
:deep(.e-dropdown-btn.profile-tabs-more-btn) {
    font-size: var(--text-size-headline);
    font-weight: var(--text-weight-medium);
    color: var(--text-color-body);
    background: transparent;
    border: none;
    box-shadow: none;
}
</style>

<style>
/* Unscoped style to target the Syncfusion popup attached to the body */
.profile-tabs-dropdown-popup.e-dropdown-popup {
    transform: translateX(-3rem) !important; /* !important is kept here just to guarantee override over Syncfusion's inline style.left/transform */
}

/* High specificity to override Syncfusion defaults without !important */
.profile-tabs-dropdown-popup.e-dropdown-popup ul .e-item,
.profile-tabs-dropdown-popup.e-dropdown-popup ul .e-item .e-menu-icon {
    font-size: var(--text-size-headline);
    color: var(--grayscale-700);
}
</style>
